import React from 'react';
import { motion } from 'framer-motion'; 
import ProjectStatsBadge from '../../../components/ProjectStatsBadge';
import ShareProjectButton from '../../../components/ShareProjectButton/ShareProjectButton';
import './instagram.css'; 
import instagramCloneImage from '../../../assets/images/instagramuiclone.png';

const InstagramUIClone = () => {
  return (
    <motion.div 
      className="project-detail-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <motion.h1 
        className="project-heading"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        Instagram <span>UI Clone</span>
      </motion.h1>

      <motion.p 
        className="last-updated"
        initial={{ x: 50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        <br />
        Last Updated On: 11th July 2026
      </motion.p>

      <div className="project-content">
        <motion.div 
          className="project-left"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <img src={instagramCloneImage} alt="Instagram UI Clone" className="project-image" />
          <h2 className="project-subtitle">Instagram Clone</h2>
          <span className="project-category">Website</span>
          <p className="project-author">Author: Praveen Kumar B</p>

          <ProjectStatsBadge docId="instagramClone" trackView={true} />

          {/* Buttons */}
          <div className="project-buttons">
            <a href="https://project-tap-assignment-7.netlify.app/" className="visit-project-button">
              → Visit the Project
            </a>
            <a href="https://github.com/Praveenkumar-king/Instagram-UI-Clone" className="visit-project-button">
              → View on GitHub
            </a>
            <ShareProjectButton
              project={{
                id: "instagramClone",
                title: "Instagram UI Clone",
                path: "/projects/projectdetail/instagram-clone"
              }}
            />
          </div>
        </motion.div>

        <motion.div 
          className="project-right"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <h3 className="section-title">Project Details</h3>
          <p className="overview-text">
            Welcome to the <strong>Instagram UI Clone</strong>! Here's a detailed overview of the social media platform interface.
          </p>

          <h3 className="section-title">Overview 🌟</h3>
          <p className="overview-description">
            The <strong>Instagram UI Clone</strong> is a modern and responsive social media application inspired by Instagram. It recreates the familiar social experience with a clean layout and dynamic feel.
          </p>

          <h3 className="section-title">Key Features 🛠️</h3>
          <ul className="feature-list">
            <li><strong>Home Feed</strong> 📸 - Browse posts from friends and creators with an engaging layout.</li>
            <li><strong>Stories Section</strong> 🟢 - View user stories with a modern horizontal carousel.</li>
            <li><strong>Profile Pages</strong> 👤 - Display profile information, posts, followers, and following.</li>
            <li><strong>Explore Page</strong> 🔍 - Discover trending photos and videos in a responsive grid.</li>
            <li><strong>Reels Interface</strong> 🎥 - Showcase short-form video content with an immersive design.</li>
            <li><strong>Responsive Navigation</strong> 📱 - Optimized navigation for desktop, tablet, and mobile devices.</li>
            <li><strong>Dark Theme UI</strong> 🌙 - Modern Instagram-inspired dark interface with smooth animations.</li>
          </ul>

          <h3 className="section-title">How the Website Works 🔍</h3>
          <ul className="how-it-works-list">
            <li><strong>Browse Feed</strong> 📰 - View posts from followed accounts.</li>
            <li><strong>Watch Stories</strong> 📸 - Explore recent stories from users.</li>
            <li><strong>Discover Content</strong> 🔍 - Find trending posts and creators in the Explore section.</li>
            <li><strong>Visit Profiles</strong> 👤 - Open user profiles to view posts and account details.</li>
          </ul>

          <h3 className="section-title">Features & Benefits 🚀</h3>
          <ul className="benefits-list">
            <li><strong>Modern User Interface</strong> 🎨 - Premium Instagram-inspired design.</li>
            <li><strong>Responsive Experience</strong> 📱 - Smooth browsing across all screen sizes.</li>
            <li><strong>Interactive Components</strong> ❤️ - Like, comment, share, and save button interfaces.</li>
            <li><strong>Enhanced User Experience</strong> ✨ - Clean layouts with engaging animations and transitions.</li>
          </ul>

          <h3 className="section-title">Technical Details ⚙️</h3>
          <ul className="technical-details-list">
            <li><strong>Frontend</strong> 🖥️ - React, HTML5, CSS3, and JavaScript (ES6).</li>
            <li><strong>Animations</strong> ✨ - Framer Motion for smooth transitions and page animations.</li>
            <li><strong>Responsive Design</strong> 📱 - CSS Flexbox, Grid, and Media Queries.</li>
            <li><strong>Deployment</strong> ☁️ - Hosted on Netlify with GitHub for version control.</li>
          </ul>

          <h3 className="section-title">Real-Life Applications 🌍</h3>
          <ul className="real-life-applications-list">
            <li><strong>Social Networking Platforms</strong> 🌍 - Community-based social media applications.</li>
            <li><strong>Photo Sharing Platforms</strong> 📷 - Image and media sharing websites.</li>
            <li><strong>Content Creator Portfolios</strong> 🎬 - Showcase creative work through engaging layouts.</li>
            <li><strong>Learning Modern UI Design</strong> 💻 - Practice responsive social media interface development.</li>
          </ul>

          <h3 className="section-title">Future Features 🔮</h3>
          <ul className="future-features-list">
            <li><strong>User Authentication</strong> 🔐 - Secure login and account management.</li>
            <li><strong>Real-Time Messaging</strong> 💬 - Direct messaging between users.</li>
            <li><strong>Stories & Reels Upload</strong> 🎥 - Create and upload stories and short videos.</li>
            <li><strong>Notifications</strong> 🔔 - Real-time alerts for likes, comments, and followers.</li>
          </ul>

          <h3 className="section-title">Conclusion 🎯</h3>
          <p>
            The <strong>Instagram UI Clone</strong> recreates the modern social media experience with responsive layouts, engaging feeds, profile pages, stories, and interactive navigation. This project demonstrates advanced front-end development skills, responsive UI design, and the implementation of a professional social networking interface.
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default InstagramUIClone;